import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowUpDown, Plus, ArrowLeft, TrendingUp, TrendingDown, Package, AlertTriangle } from 'lucide-react';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StockMovementsPage() {
  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    item_id: '',
    location_id: '',
    movement_type: 'ADJUST',
    qty: 0,
    unit_cost: '',
    reason: '',
    notes: ''
  });

  const { token = localStorage.getItem('backoffice_token') } = useOutletContext() || {};
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useLatestCallback(async () => {
    setLoading(true);
    try {
      const [movRes, itemsRes, locsRes] = await Promise.all([
        fetch(`${API_URL}/api/inv/movements?limit=100`, { headers }),
        fetch(`${API_URL}/api/inv/items?limit=200`, { headers }),
        fetch(`${API_URL}/api/inv/locations`, { headers })
      ]);
      
      if (movRes.ok) setMovements(await movRes.json());
      if (itemsRes.ok) setItems(await itemsRes.json());
      if (locsRes.ok) setLocations(await locsRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/inv/movements`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          qty: parseFloat(formData.qty),
          unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null
        })
      });
      
      if (res.ok) {
        setShowDialog(false);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Error creating movement');
      }
    } catch (err) {
      console.error('Error creating movement:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      item_id: '',
      location_id: '',
      movement_type: 'ADJUST',
      qty: 0,
      unit_cost: '',
      reason: '',
      notes: ''
    });
  };

  const getMovementIcon = (type) => {
    const icons = {
      'RECEIVE': <TrendingUp className="w-4 h-4 text-green-400" />,
      'CONSUME': <TrendingDown className="w-4 h-4 text-blue-400" />,
      'WASTE': <AlertTriangle className="w-4 h-4 text-red-400" />,
      'ADJUST': <ArrowUpDown className="w-4 h-4 text-yellow-400" />,
      'TRANSFER_IN': <Package className="w-4 h-4 text-green-400" />,
      'TRANSFER_OUT': <Package className="w-4 h-4 text-orange-400" />,
      'COUNT': <Package className="w-4 h-4 text-purple-400" />
    };
    return icons[type] || <ArrowUpDown className="w-4 h-4 text-gray-400" />;
  };

  const getMovementColor = (type) => {
    const colors = {
      'RECEIVE': 'bg-green-600',
      'CONSUME': 'bg-blue-600',
      'WASTE': 'bg-red-600',
      'ADJUST': 'bg-yellow-600',
      'TRANSFER_IN': 'bg-green-600',
      'TRANSFER_OUT': 'bg-orange-600',
      'COUNT': 'bg-purple-600'
    };
    return colors[type] || 'bg-gray-600';
  };

  const filteredMovements = movements.filter(m =>
    typeFilter === 'all' || m.movement_type === typeFilter
  );

  const movementTypes = ['RECEIVE', 'CONSUME', 'WASTE', 'ADJUST', 'COUNT'];

  return (
    <div className="p-6 space-y-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="stock-movements-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <a href={bo('inventory')} className="p-2 hover:bg-gray-800 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </a>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">Stock Movements</h1>
          <p className="text-gray-400">Track all inventory changes and adjustments</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-movement-btn">
              <Plus className="w-4 h-4 mr-2" /> Record Movement
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Record Stock Movement</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300">Item *</Label>
                <Select
                  value={formData.item_id}
                  onValueChange={(v) => setFormData({ ...formData, item_id: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue placeholder="Select item" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 max-h-[200px]">
                    {items.map(item => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} {item.sku ? `(${item.sku})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Location *</Label>
                  <Select
                    value={formData.location_id}
                    onValueChange={(v) => setFormData({ ...formData, location_id: v })}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Movement Type *</Label>
                  <Select
                    value={formData.movement_type}
                    onValueChange={(v) => setFormData({ ...formData, movement_type: v })}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {movementTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Quantity *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.qty}
                    onChange={(e) => setFormData({ ...formData, qty: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="Use negative for decreases"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Use negative for decreases (CONSUME, WASTE)</p>
                </div>
                <div>
                  <Label className="text-gray-300">Unit Cost ($)</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={formData.unit_cost}
                    onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    placeholder="For RECEIVE"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Reason</Label>
                <Input
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Reason for adjustment..."
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Additional notes..."
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!formData.item_id || !formData.location_id}>
                  Record Movement
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48 bg-gray-800 border-gray-700">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="all">All Types</SelectItem>
            {movementTypes.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Movements Table */}
      <Card className="bg-gray-900/50 border-gray-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="text-left p-4 text-gray-400 font-medium">Date/Time</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Type</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Item</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Location</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Quantity</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Unit Cost</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Total</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Reason</th>
                  <th className="text-left p-4 text-gray-400 font-medium">By</th>
                </tr>
              </thead>
              <tbody>
                {filteredMovements.map(m => (
                  <tr key={m.id} className="border-t border-gray-700 hover:bg-gray-800/30">
                    <td className="p-4 text-gray-400 text-sm">
                      {new Date(m.created_at).toLocaleString()}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {getMovementIcon(m.movement_type)}
                        <Badge className={getMovementColor(m.movement_type)}>
                          {m.movement_type}
                        </Badge>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="text-white font-medium">{m.item_name || '-'}</p>
                    </td>
                    <td className="p-4 text-gray-400">{m.location_name || '-'}</td>
                    <td className={`p-4 text-right font-medium ${m.qty >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {m.qty >= 0 ? '+' : ''}{m.qty?.toFixed(2)}
                    </td>
                    <td className="p-4 text-right text-gray-400">
                      {m.unit_cost ? `$${m.unit_cost.toFixed(4)}` : '-'}
                    </td>
                    <td className="p-4 text-right text-gray-400">
                      {m.total_cost ? `$${m.total_cost.toFixed(2)}` : '-'}
                    </td>
                    <td className="p-4 text-gray-400 text-sm max-w-[150px] truncate">
                      {m.reason || '-'}
                    </td>
                    <td className="p-4 text-gray-400 text-sm">{m.actor_name || '-'}</td>
                  </tr>
                ))}
                {filteredMovements.length === 0 && (
                  <tr>
                    <td colSpan="9" className="p-8 text-center text-gray-400">
                      {loading ? 'Loading...' : 'No movements found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
